import React, { useMemo, useState, useEffect } from "react";
import ModalView from "../../common/modal/index.js";
import Button from "../../common/button/button.js";
import InsuranceStatusSelector from "../../common/insurance-status-selector/index.js";
import Text from "../../common/typography/text.js";
import "./MakeInvoicePaymentModal.css";

const CARD_SURCHARGE_PERCENT = 0.029;
const CARD_FIXED_FEE_CENTS = 30;
const BANK_SURCHARGE_PERCENT = 0.008;
const BANK_SURCHARGE_CAP_CENTS = 500;

const roundCurrency = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const toCents = (value) => Math.round(roundCurrency(value) * 100);

const formatCurrency = (value) =>
  roundCurrency(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const calculateProcessingFeeCents = (amount, paymentMethodType) => {
  const subtotalCents = toCents(amount);
  if (subtotalCents <= 0) return 0;
  if (paymentMethodType === "us_bank_account") {
    return Math.min(
      BANK_SURCHARGE_CAP_CENTS,
      Math.round(subtotalCents * BANK_SURCHARGE_PERCENT),
    );
  }
  return Math.round(
    (subtotalCents + CARD_FIXED_FEE_CENTS) / (1 - CARD_SURCHARGE_PERCENT) -
      subtotalCents,
  );
};

const MakeInvoicePaymentModal = ({
  isOpen,
  onClose,
  invoiceData,
  invoiceTotal,
  totalPaymentsApplied,
  openBalance,
  statusText,
  onCheckout,
  isSubmitting,
}) => {
  const [paymentMode, setPaymentMode] = useState("partial");
  const [paymentMethodType, setPaymentMethodType] = useState("card");
  const [amount, setAmount] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPaymentMode("partial");
      setPaymentMethodType("card");
      setAmount("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (paymentMode === "full") {
      setAmount(String(roundCurrency(openBalance)));
    }
  }, [paymentMode, openBalance]);

  const normalizedAmount = roundCurrency(amount);
  const amountError = useMemo(() => {
    if (amount === "" || amount === null || amount === undefined) {
      return "Enter a payment amount.";
    }
    if (!Number.isFinite(Number(amount)) || normalizedAmount <= 0) {
      return "Payment amount must be greater than 0.";
    }
    if (normalizedAmount > roundCurrency(openBalance)) {
      return "Payment amount cannot exceed open balance.";
    }
    return "";
  }, [amount, normalizedAmount, openBalance]);

  const processingFeeCents = calculateProcessingFeeCents(
    normalizedAmount,
    paymentMethodType,
  );
  const processingFee = processingFeeCents / 100;
  const remainingBalance = roundCurrency(Math.max(openBalance - normalizedAmount, 0));
  const grandTotal = roundCurrency(normalizedAmount + processingFee);
  const processingFeeLabel =
    paymentMethodType === "us_bank_account"
      ? "Bank Processing Fee"
      : "Card Processing Fee";

  const handleSubmit = () => {
    if (amountError) return;
    onCheckout({
      amount: normalizedAmount,
      paymentMethodType,
      expectedOpenBalance: roundCurrency(openBalance),
    });
  };

  return (
    <ModalView
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Make a Payment"
      minWidth="640px"
      maxWidth="640px"
      parentClassname="make-invoice-payment-modal"
      textInputComponent={
        <div className="invoice-payment-modal-body">
          <div className="invoice-payment-heading">
            <span>Invoice</span>
            <span className="invoice-payment-muted">{invoiceData?.InvoiceNumber}</span>
            <span className="invoice-payment-status-dot" />
            <span className="invoice-payment-status">{statusText}</span>
          </div>

          <div className="invoice-payment-metrics">
            <div>
              <Text variant="tinyText" color="lightText">Invoice Total</Text>
              <div className="invoice-payment-number">{formatCurrency(invoiceTotal)}</div>
              {Number(invoiceData?.ShippingCost ?? 0) > 0 && (
                <Text variant="tinyText" color="lightText">
                  Includes Shipping Cost of {formatCurrency(invoiceData?.ShippingCost)}
                </Text>
              )}
            </div>
            <div>
              <Text variant="tinyText" color="lightText">Total Payments Applied</Text>
              <div className="invoice-payment-number">
                {formatCurrency(totalPaymentsApplied)}
              </div>
            </div>
            <div>
              <Text variant="tinyText" color="lightText">Open Balance</Text>
              <div className="invoice-payment-number">{formatCurrency(openBalance)}</div>
            </div>
          </div>

          <InsuranceStatusSelector
            activeTab={{ key: paymentMode }}
            parentClassname="invoice-payment-tabs"
            buttons={[
              {
                key: "partial",
                text: "Partial Payment",
                onClick: () => setPaymentMode("partial"),
              },
              {
                key: "full",
                text: "Pay in Full",
                onClick: () => setPaymentMode("full"),
              },
            ]}
          />

          <div className="invoice-payment-amount-row">
            <label>
              <span>Enter Amount</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                disabled={paymentMode === "full"}
                onChange={(event) => setAmount(event.target.value)}
              />
              {amountError && <small>{amountError}</small>}
            </label>
            <label>
              <span>Remaining Balance</span>
              <input type="text" value={formatCurrency(remainingBalance)} disabled />
            </label>
          </div>

          <InsuranceStatusSelector
            activeTab={{ key: paymentMethodType }}
            parentClassname="invoice-payment-tabs"
            buttons={[
              {
                key: "card",
                text: "Pay by Card",
                onClick: () => setPaymentMethodType("card"),
              },
              {
                key: "us_bank_account",
                text: "Pay by Bank Transfer",
                onClick: () => setPaymentMethodType("us_bank_account"),
              },
            ]}
          />

          <div className="invoice-payment-summary">
            <div>
              <span>Total Amount</span>
              <strong>{formatCurrency(normalizedAmount)}</strong>
            </div>
            <div>
              <span>{processingFeeLabel}</span>
              <strong>{formatCurrency(processingFee)}</strong>
            </div>
            <div className="invoice-payment-grand-total">
              <span>Grand Total</span>
              <strong>{formatCurrency(grandTotal)}</strong>
            </div>
          </div>
        </div>
      }
      footerButtonWrapper={
        <div className="invoice-payment-footer">
          <div className="invoice-payment-footer-buttons">
            <Button variant="table" subVariant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="table"
              subVariant="primary"
              onClick={handleSubmit}
              disabled={Boolean(amountError) || isSubmitting}
            >
              {isSubmitting ? "Redirecting..." : "Continue to Checkout"}
            </Button>
          </div>
          <Text variant="tinyText" color="lightText">
            You won't be charged yet. You will be redirected to a secure checkout.
          </Text>
        </div>
      }
    />
  );
};

export default MakeInvoicePaymentModal;
